// DOM Trader — what an LP's orders can carry, for the order form.
//
// The bridge has no per-LP capabilities route (GET /fix/lp/:id/capabilities
// answers 404), so the form fell back to one generic set for every LP. An LP
// listed here shows its own set instead; any other LP keeps the page's
// existing behaviour.
//
// LMAX (Broker FIX Trading API; the bridge's LMAXAdapter pre-flight):
//   Market: IOC, FOK            Limit: DAY, GTC, IOC, FOK
//   No stop-loss / take-profit on the order. Quantity in contracts, steps of
//   the instrument's minimum (0.1).

export interface DomOrderRules {
  /** Order types the form offers (Market and Limit: the DOM sends no stop orders). */
  orderTypes: string[];
  /** Time-in-force choices for an order type. */
  tifFor: (orderType: string) => string[];
  /** Stop-loss / take-profit fields shown. */
  slTp: boolean;
  /** Unit of the quantity field. */
  qtyUnit: string;
}

const LMAX: DomOrderRules = {
  orderTypes: ['MARKET', 'LIMIT'],
  tifFor: (t) => (t === 'MARKET' ? ['IOC', 'FOK'] : ['DAY', 'GTC', 'IOC', 'FOK']),
  slTp: false,
  qtyUnit: 'Contracts',
};

/** The LP's own rules, or null for an LP with none here (the page's default). */
export function domOrderRules(providerType: string | undefined, lpId: string | undefined): DomOrderRules | null {
  const p = (providerType ?? '').toLowerCase();
  if (p === 'lmax' || (!p && (lpId ?? '').toLowerCase().startsWith('lmax'))) return LMAX;
  return null;
}

/**
 * LP units per unit of a position's net_qty. TE reports net_qty in multiples
 * of the instrument's minimum (units = net_qty x min_trade_vol); LMAX reports
 * its own units, contracts, whose minimum is a fraction (0.1): units = net_qty.
 * `fallback` is the page's multiplier when the instrument is not known.
 */
export function unitsPerPositionQty(minVol: number | undefined, lpId: string | undefined, fallback: number): number {
  if (minVol != null && minVol > 0 && minVol < 1) return 1;
  if (minVol == null && (lpId ?? '').toLowerCase().startsWith('lmax')) return 1;
  return minVol ?? fallback;
}

/**
 * What is wrong with a quantity typed in the DOM Trader, '' when nothing (or
 * nothing typed yet). Same rule as the order check: an instrument whose minimum
 * is a fraction takes steps of it (LMAX: 0.1 contract); `wholeUnits` also
 * holds any other instrument to whole units, at least its minimum.
 */
export function qtyProblem(raw: string, minVol: number | undefined, wholeUnits: boolean): string {
  const s = raw.trim();
  if (!s) return '';
  const q = Number(s);
  if (!Number.isFinite(q) || q <= 0) return 'Enter a quantity above 0';
  const min = minVol ?? 0;
  const fractional = min > 0 && min < 1;
  if (!fractional && !wholeUnits) return '';
  const step = fractional ? min : 1;
  const nearest = Number((Math.round(q / step) * step).toFixed(8));
  if (min > 0 && q < min) return `Below the minimum of ${min.toLocaleString()}`;
  if (Math.abs(nearest - q) > 1e-9) return `Must be in steps of ${step.toLocaleString()} (nearest: ${nearest.toLocaleString()})`;
  return '';
}
